/**
 * Listas editáveis do protocolo (E74.F1): eventos da trilha de aceite e
 * endereços IP. Cada linha é um item do array no estado do `ProtocoloForm`.
 */
import { Plus, Trash2 } from 'lucide-react';

import { Button, Field, Input, Select } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { PAPEIS_IP, TIPOS_EVENTO } from '@/lib/exame_eletronico';

import { eventoVazio, ipVazio, type EventoForm, type IpForm } from './protocolo_form_state';
import { CampoRegistroDaAssinatura } from './ProtocoloFormRegistro';
import type { SecaoProps } from './ProtocoloFormSecoes';

const t = ptBR.exameEletronico.form;

function Cabecalho({ titulo, hint }: { titulo: string; hint?: string }) {
  return (
    <div className="mb-sc-2">
      <p className="font-titulo text-[13px] uppercase tracking-[0.06em] text-fg-muted">{titulo}</p>
      {hint !== undefined ? <p className="mt-1 text-caption text-fg-dim">{hint}</p> : null}
    </div>
  );
}

function substituir<T>(lista: T[], indice: number, item: T): T[] {
  return lista.map((atual, i) => (i === indice ? item : atual));
}

function remover<T>(lista: T[], indice: number): T[] {
  return lista.filter((_, i) => i !== indice);
}

interface LinhaEventoProps {
  idPrefix: string;
  indice: number;
  evento: EventoForm;
  disabled: boolean;
  onChange: (evento: EventoForm) => void;
  onRemove: () => void;
}

function LinhaEvento({ idPrefix, indice, evento, disabled, onChange, onRemove }: LinhaEventoProps) {
  const id = `${idPrefix}-evento-${indice}`;
  return (
    <li
      className="grid gap-sc-3 border border-border bg-surface-2 p-sc-3 sm:grid-cols-[1fr_1fr_1fr_auto]"
      data-testid={id}
    >
      <Field htmlFor={`${id}-tipo`} label={t.eventoTipo}>
        <Select
          id={`${id}-tipo`}
          data-testid={`${id}-tipo`}
          value={evento.tipo}
          disabled={disabled}
          onChange={(e) => onChange({ ...evento, tipo: e.target.value as EventoForm['tipo'] })}
        >
          {TIPOS_EVENTO.map((tipo) => (
            <option key={tipo} value={tipo}>
              {t.eventoTipoOpcoes[tipo]}
            </option>
          ))}
        </Select>
      </Field>
      <Field htmlFor={`${id}-utc`} label={t.eventoEmUtc}>
        <Input
          id={`${id}-utc`}
          data-testid={`${id}-utc`}
          type="datetime-local"
          className="font-mono"
          value={evento.emUtc}
          disabled={disabled}
          onChange={(e) => onChange({ ...evento, emUtc: e.target.value })}
        />
      </Field>
      <Field htmlFor={`${id}-local`} label={t.eventoEmLocal}>
        <Input
          id={`${id}-local`}
          data-testid={`${id}-local`}
          type="datetime-local"
          className="font-mono"
          value={evento.emLocal}
          disabled={disabled}
          onChange={(e) => onChange({ ...evento, emLocal: e.target.value })}
        />
      </Field>
      <div className="flex items-end">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled}
          onClick={onRemove}
          aria-label={t.eventoRemover}
          data-testid={`${id}-remover`}
        >
          <Trash2 className="h-4 w-4" aria-hidden />
        </Button>
      </div>
      <Field htmlFor={`${id}-descricao`} label={t.eventoDescricao} className="sm:col-span-4">
        <Input
          id={`${id}-descricao`}
          data-testid={`${id}-descricao`}
          value={evento.descricao}
          disabled={disabled}
          maxLength={280}
          onChange={(e) => onChange({ ...evento, descricao: e.target.value })}
        />
      </Field>
    </li>
  );
}

export function SecaoEventos({ idPrefix, state, patch, disabled, erros }: SecaoProps) {
  return (
    <section>
      <Cabecalho titulo={t.secaoEventos} hint={t.eventoLocalHint} />
      <CampoRegistroDaAssinatura
        idPrefix={idPrefix}
        state={state}
        patch={patch}
        disabled={disabled}
      />
      {state.eventos.length === 0 ? (
        <p className="text-body-sm text-fg-dim">{t.eventoVazio}</p>
      ) : (
        <ul className="space-y-sc-2">
          {state.eventos.map((evento, indice) => (
            <LinhaEvento
              key={indice}
              idPrefix={idPrefix}
              indice={indice}
              evento={evento}
              disabled={disabled}
              onChange={(novo) => patch({ eventos: substituir(state.eventos, indice, novo) })}
              onRemove={() => patch({ eventos: remover(state.eventos, indice) })}
            />
          ))}
        </ul>
      )}
      {erros.eventos !== undefined ? (
        <p className="mt-sc-2 text-caption text-danger" role="alert">
          {erros.eventos}
        </p>
      ) : null}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="mt-sc-2"
        disabled={disabled}
        onClick={() => patch({ eventos: [...state.eventos, eventoVazio()] })}
        data-testid={`${idPrefix}-evento-adicionar`}
      >
        <Plus className="h-4 w-4" aria-hidden />
        {t.eventoAdicionar}
      </Button>
    </section>
  );
}

interface LinhaIpProps {
  idPrefix: string;
  indice: number;
  ip: IpForm;
  disabled: boolean;
  onChange: (ip: IpForm) => void;
  onRemove: () => void;
}

function LinhaIp({ idPrefix, indice, ip, disabled, onChange, onRemove }: LinhaIpProps) {
  const id = `${idPrefix}-ip-${indice}`;
  return (
    <li
      className="grid gap-sc-3 border border-border bg-surface-2 p-sc-3 sm:grid-cols-[2fr_1fr_auto]"
      data-testid={id}
    >
      <Field htmlFor={`${id}-endereco`} label={t.ipEndereco}>
        <Input
          id={`${id}-endereco`}
          data-testid={`${id}-endereco`}
          className="font-mono"
          value={ip.endereco}
          disabled={disabled}
          maxLength={45}
          onChange={(e) => onChange({ ...ip, endereco: e.target.value })}
        />
      </Field>
      <Field htmlFor={`${id}-papel`} label={t.ipPapel}>
        <Select
          id={`${id}-papel`}
          data-testid={`${id}-papel`}
          value={ip.papel}
          disabled={disabled}
          onChange={(e) => onChange({ ...ip, papel: e.target.value as IpForm['papel'] })}
        >
          {PAPEIS_IP.map((papel) => (
            <option key={papel} value={papel}>
              {t.ipPapelOpcoes[papel]}
            </option>
          ))}
        </Select>
      </Field>
      <div className="flex items-end">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled}
          onClick={onRemove}
          aria-label={t.ipRemover}
          data-testid={`${id}-remover`}
        >
          <Trash2 className="h-4 w-4" aria-hidden />
        </Button>
      </div>
    </li>
  );
}

export function SecaoIps({ idPrefix, state, patch, disabled }: SecaoProps) {
  return (
    <section>
      <Cabecalho titulo={t.secaoIps} />
      {state.ips.length === 0 ? (
        <p className="text-body-sm text-fg-dim">{t.ipVazio}</p>
      ) : (
        <ul className="space-y-sc-2">
          {state.ips.map((ip, indice) => (
            <LinhaIp
              key={indice}
              idPrefix={idPrefix}
              indice={indice}
              ip={ip}
              disabled={disabled}
              onChange={(novo) => patch({ ips: substituir(state.ips, indice, novo) })}
              onRemove={() => patch({ ips: remover(state.ips, indice) })}
            />
          ))}
        </ul>
      )}
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="mt-sc-2"
        disabled={disabled}
        onClick={() => patch({ ips: [...state.ips, ipVazio()] })}
        data-testid={`${idPrefix}-ip-adicionar`}
      >
        <Plus className="h-4 w-4" aria-hidden />
        {t.ipAdicionar}
      </Button>
    </section>
  );
}
